import { Code, ConnectError } from '@bufbuild/connect';
import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import api from '@/shared/api/api';
import { Asset } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { DentalPhotoAlbum } from '@/shared/api/protocol-ts/model/dto_dental_photo_album_pb';
import { OrganizationStaff } from '@/shared/api/protocol-ts/model/dto_organization_staff_pb';
import { Report } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Tooth } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { SharingInvitation } from '@/shared/api/protocol-ts/model/dto_sharing_pb';
import { Study } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { PATHS, StreamDataAccumulatorKey } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { debug, isConnectNetworkError } from '@/shared/lib';
import { toastCaller } from '@/shared/ui';

import { accessModel } from '@/entities/access';
import { albumModel } from '@/entities/album';
import { assetsModel } from '@/entities/assets';
import { organizationModel } from '@/entities/organization';
import { organizationStaffModel } from '@/entities/organizationStaff';
import { patientModel } from '@/entities/patient';
import { reportsModel } from '@/entities/reports';
import { studyModel } from '@/entities/study';
import { toothModel } from '@/entities/tooth';

import { highLoadModel } from '@/features/highLoad';

type PatientProfileStreamDataAccumulators = {
  [StreamDataAccumulatorKey.study]: Study[];
  [StreamDataAccumulatorKey.reports]: Report[];
  [StreamDataAccumulatorKey.teeth]: Tooth[];
  [StreamDataAccumulatorKey.assets]: Asset[];
  [StreamDataAccumulatorKey.patientProfileGuardedAssetsIDs]: string[];
  [StreamDataAccumulatorKey.access]: SharingInvitation[];
  [StreamDataAccumulatorKey.dentalPhotoAlbum]: DentalPhotoAlbum[];
  [StreamDataAccumulatorKey.organizationStaff]: OrganizationStaff[];
};

const dataAccumulators: PatientProfileStreamDataAccumulators = {
  [StreamDataAccumulatorKey.study]: [],
  [StreamDataAccumulatorKey.reports]: [],
  [StreamDataAccumulatorKey.teeth]: [],
  [StreamDataAccumulatorKey.assets]: [],
  [StreamDataAccumulatorKey.patientProfileGuardedAssetsIDs]: [],
  [StreamDataAccumulatorKey.access]: [],
  [StreamDataAccumulatorKey.dentalPhotoAlbum]: [],
  [StreamDataAccumulatorKey.organizationStaff]: [],
};

let abortController: AbortController;
let patientOrganizationID: string | null = null;

export const usePatientProfileStream = (patientID: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();

  const currentOrganizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const allOrganizationsIDs = useAppSelector(
    organizationModel.selectors.selectIds,
  );
  const currentOrganizationIDRef = useRef(currentOrganizationID);
  const allOrganizationsIDsRef = useRef(allOrganizationsIDs);

  currentOrganizationIDRef.current = currentOrganizationID;
  allOrganizationsIDsRef.current = allOrganizationsIDs;

  const openPatientProfileStream = async (silent = false) => {
    const streamAbortController = new AbortController();

    abortController = streamAbortController;

    dispatch(assetsModel.actions.startPatientAssetsSnapshot({ patientID }));

    if (!silent) {
      dispatch(patientModel.actions.setLoading('pending'));
      dispatch(studyModel.actions.setLoading('pending'));
      dispatch(reportsModel.actions.setLoading('pending'));
      dispatch(assetsModel.actions.setLoading('pending'));
      dispatch(accessModel.sharingActions.setLoading('pending'));
      dispatch(organizationModel.actions.setLoading('pending'));
      dispatch(albumModel.actions.setLoading('pending'));
    }

    try {
      const patientProfileStream = api.patient.patientProfileStream(
        { PatientID: patientID },
        { signal: streamAbortController.signal },
      );

      for await (const { Update } of patientProfileStream) {
        switch (Update.case) {
          case 'HistoricalPatient': {
            if (
              allOrganizationsIDsRef.current.includes(
                Update.value.OrganizationID,
              )
            ) {
              dispatch(
                organizationModel.thunks.setCurrentOrganizationByID(
                  Update.value.OrganizationID,
                ),
              );
            }

            dispatch(patientModel.actions.addOne(Update.value));
            dispatch(patientModel.actions.setCurrentPatient(Update.value));
            dispatch(patientModel.actions.setLoading('succeeded'));
            break;
          }

          case 'UpdatedPatient': {
            dispatch(patientModel.actions.setNewestOne(Update.value));
            dispatch(patientModel.actions.updateCurrentPatient(Update.value));
            break;
          }

          // NOTE: Fetching data about the organization the patient belongs to is used in cases where the user is not a member of that organization (e.g. admin or a shared doctor)
          case 'HistoricalOrganization': {
            patientOrganizationID = Update.value.ID;

            dispatch(
              organizationModel.actions.setPatientOrganization(Update.value),
            );

            dispatch(organizationModel.actions.setLoading('succeeded'));
            break;
          }

          // NOTE: Fetching data about the organization the patient belongs to is used in cases where the user is not a member of that organization (e.g. admin or a shared doctor)
          case 'UpdatedOrganization': {
            dispatch(
              organizationModel.actions.setPatientOrganization(Update.value),
            );

            break;
          }

          case 'HistoricalStudy': {
            dataAccumulators[StreamDataAccumulatorKey.study].push(Update.value);
            break;
          }

          case 'EndOfHistoricalStudies': {
            dispatch(
              studyModel.actions.setNewestMany(
                dataAccumulators[StreamDataAccumulatorKey.study],
              ),
            );
            dispatch(studyModel.actions.setLoading('succeeded'));
            dataAccumulators[StreamDataAccumulatorKey.study] = [];
            break;
          }

          case 'UpdatedStudy': {
            dispatch(studyModel.actions.setNewestOne(Update.value));
            break;
          }

          case 'HistoricalReport': {
            dataAccumulators[StreamDataAccumulatorKey.reports].push(
              Update.value,
            );
            break;
          }

          case 'EndOfHistoricalReports': {
            dispatch(
              // For historical entities use only addMany function and setMany for updated entities only
              reportsModel.actions.addMany(
                dataAccumulators[StreamDataAccumulatorKey.reports],
              ),
            );
            dispatch(reportsModel.actions.setLoading('succeeded'));
            dataAccumulators[StreamDataAccumulatorKey.reports] = [];
            break;
          }

          case 'UpdatedReport': {
            dispatch(reportsModel.actions.setNewestOne(Update.value));
            break;
          }

          case 'HistoricalTooth': {
            dataAccumulators[StreamDataAccumulatorKey.teeth].push(Update.value);
            break;
          }

          case 'EndOfHistoricalTeeth': {
            dispatch(
              toothModel.actions.addMany(
                dataAccumulators[StreamDataAccumulatorKey.teeth],
              ),
            );
            dataAccumulators[StreamDataAccumulatorKey.teeth] = [];
            break;
          }

          case 'HistoricalAsset': {
            dataAccumulators[StreamDataAccumulatorKey.assets].push(
              Update.value,
            );
            dataAccumulators.patientProfileGuardedAssetsIDs.push(
              Update.value.ID,
            );
            break;
          }

          case 'UpdatedAsset': {
            dispatch(assetsModel.actions.setNewestOne(Update.value));
            dispatch(assetsModel.actions.addGuardedIDs([Update.value.ID]));
            break;
          }

          case 'EndOfHistoricalAssets': {
            dispatch(
              assetsModel.actions.setMany(
                dataAccumulators[StreamDataAccumulatorKey.assets],
              ),
            );
            dispatch(
              assetsModel.actions.initGuardedIDs(
                dataAccumulators.patientProfileGuardedAssetsIDs,
              ),
            );
            dispatch(assetsModel.actions.setLoading('succeeded'));
            dispatch(
              assetsModel.actions.completePatientAssetsSnapshot({ patientID }),
            );
            dataAccumulators.patientProfileGuardedAssetsIDs = [];
            dataAccumulators[StreamDataAccumulatorKey.assets] = [];
            break;
          }

          case 'HistoricalSharing': {
            dataAccumulators[StreamDataAccumulatorKey.access].push(
              Update.value,
            );
            break;
          }

          case 'EndOfHistoricalShares': {
            dispatch(
              accessModel.sharingActions.addMany(
                dataAccumulators[StreamDataAccumulatorKey.access],
              ),
            );

            dispatch(accessModel.sharingActions.setLoading('succeeded'));

            dataAccumulators[StreamDataAccumulatorKey.access] = [];
            break;
          }

          case 'UpdatedSharing': {
            dispatch(accessModel.sharingActions.setNewestOne(Update.value));
            break;
          }

          case 'HistoricalDentalPhotoAlbum': {
            dataAccumulators[StreamDataAccumulatorKey.dentalPhotoAlbum].push(
              Update.value,
            );
            break;
          }

          case 'EndOfHistoricalDentalPhotoAlbums': {
            dispatch(
              albumModel.actions.setMany(
                dataAccumulators[StreamDataAccumulatorKey.dentalPhotoAlbum],
              ),
            );

            dispatch(albumModel.actions.setLoading('succeeded'));

            dataAccumulators[StreamDataAccumulatorKey.dentalPhotoAlbum] = [];
            break;
          }

          case 'UpdatedDentalPhotoAlbum': {
            dispatch(albumModel.actions.setNewestOne(Update.value));
            break;
          }

          case 'HistoricalOrganizationStaff': {
            dataAccumulators[StreamDataAccumulatorKey.organizationStaff].push(
              Update.value,
            );
            break;
          }

          case 'EndOfHistoricalOrganizationStaff': {
            // Replace the entire snapshot so staff deleted while offline are removed
            if (patientOrganizationID) {
              dispatch(
                organizationStaffModel.actions.removeByOrganizationID(
                  patientOrganizationID,
                ),
              );
            }
            dispatch(
              organizationStaffModel.actions.setMany(
                dataAccumulators[StreamDataAccumulatorKey.organizationStaff],
              ),
            );
            dataAccumulators[StreamDataAccumulatorKey.organizationStaff] = [];
            break;
          }

          case 'UpdatedOrganizationStaff': {
            dispatch(organizationStaffModel.actions.setNewestOne(Update.value));
            break;
          }

          case 'UpdatedHighLoadStatus': {
            dispatch(
              highLoadModel.actions.setUploadLevels({
                uploadKafkaLevel: Update.value.UploadKafka,
                uploadCBCTLevel: Update.value.UploadCBCT,
              }),
            );
            break;
          }

          case 'UpdatedReportHighLoadStatus': {
            dispatch(
              highLoadModel.actions.setReportLevels({
                reportID: Update.value.ReportID,
                levels: {
                  orgPendingLevel: Update.value.PipelineOrgPending,
                  orgsCountLevel: Update.value.PipelineOrgsCount,
                },
              }),
            );
            break;
          }
        }
      }
    } catch (error) {
      // NOTE: Cancellation is not a failure: the abort comes from unmount or from
      // a silent reconnect that has already opened a fresh stream for this patient.
      if (streamAbortController.signal.aborted) {
        return;
      }

      if (error instanceof ConnectError) {
        if (isConnectNetworkError(error)) {
          debug('Network error detected, attempting to restart stream...');
          closePatientProfileStream(false);
          openPatientProfileStream(true);
        }

        const errorCode = error.code;

        if (errorCode === Code.Unauthenticated) {
          navigate(PATHS.signIn, {
            state: { from: location?.pathname + location?.search },
          });
        }

        if (
          errorCode === Code.NotFound ||
          errorCode === Code.FailedPrecondition || // backend returns this code when patient is deleted
          errorCode === Code.PermissionDenied
        ) {
          toastCaller({
            type: 'error',
            message: formatMessage({
              id: 'reportStream.notExistOrNoAccess',
              defaultMessage:
                'The page does not exist, or you do not have permission to access this page',
            }),
            autoClose: 10000,
          });

          navigate(PATHS.patients);
        }
      }

      if (!(error instanceof ConnectError) || !isConnectNetworkError(error)) {
        // NOTE: A dead stream has to release every gate `open` raised, not just the
        // patient one: whatever stays at 'pending' hangs forever, and the patient
        // workspace waits on the organization gate while the preview document and the
        // report pages wait on the reports one.
        dispatch(patientModel.actions.setLoading('failed'));
        dispatch(studyModel.actions.setLoading('failed'));
        dispatch(reportsModel.actions.setLoading('failed'));
        dispatch(assetsModel.actions.setLoading('failed'));
        dispatch(accessModel.sharingActions.setLoading('failed'));
        dispatch(organizationModel.actions.setLoading('failed'));
        dispatch(albumModel.actions.setLoading('failed'));
        dispatch(assetsModel.actions.failPatientAssetsSnapshot({ patientID }));
      }
    }
  };

  const closePatientProfileStream = (resetStore = true) => {
    if (abortController) {
      abortController.abort();
    }

    if (resetStore) {
      dispatch(assetsModel.actions.reset());
      dispatch(reportsModel.actions.reset());
      dispatch(studyModel.actions.removeAll());
      dispatch(toothModel.actions.removeAll());
      dispatch(albumModel.actions.removeAll());
      dispatch(highLoadModel.actions.reset());

      if (
        patientOrganizationID &&
        patientOrganizationID !== currentOrganizationIDRef.current
      ) {
        dispatch(
          organizationStaffModel.actions.removeByOrganizationID(
            patientOrganizationID,
          ),
        );
      }
      patientOrganizationID = null;

      dispatch(organizationModel.actions.setPatientOrganization(null));
      dispatch(patientModel.actions.removeCurrentPatient());
    }
  };

  const reconnectPatientProfileStream = useCallback(() => {
    debug('Reconnecting patient profile stream silently...');
    closePatientProfileStream(false);
    openPatientProfileStream(true);
  }, [patientID]);

  useEffect(() => {
    openPatientProfileStream();

    return () => {
      closePatientProfileStream();
    };
  }, [patientID]);

  return { reconnectPatientProfileStream };
};
